class VivazzSizeSheet extends HTMLElement {
  #controller;
  #busy = false;
  #observer;
  #returnFocus;

  get picker() {
    return document.querySelector(`variant-picker[data-section-id="${CSS.escape(this.dataset.sectionId)}"][data-product-id="${CSS.escape(this.dataset.productId)}"]`);
  }
  get select() { return this.picker?.querySelector('[data-vivazz-size-select] select'); }
  get dialog() { return this.querySelector('dialog'); }
  get mainButton() { return document.getElementById(`BuyButtons-ProductForm-${this.dataset.sectionId}`)?.querySelector('[name="add"]'); }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    document.addEventListener('click', this.#documentClick, { signal });
    document.addEventListener('invalid', this.#invalid, { capture: true, signal });
    document.addEventListener('variant:selected', this.#onSelected, { signal });
    document.addEventListener('variant:update', this.#onUpdated, { signal });
    document.addEventListener('vivazz:size-choice', (event) => {
      if (event.target === this.picker) this.#refresh();
    }, { signal });
    document.addEventListener('vivazz:variant-error', this.#onError, { signal });
    this.addEventListener('click', this.#click, { signal });
    this.dialog.addEventListener('close', () => {
      this.picker?.querySelector('[data-size-sheet-open]')?.setAttribute('aria-expanded', 'false');
      this.#returnFocus?.isConnected && this.#returnFocus.focus({ preventScroll: true });
    }, { signal });
    this.#observer = new MutationObserver(() => this.#refresh());
    if (this.picker) this.#observer.observe(this.picker, { childList: true, subtree: true });
    queueMicrotask(() => this.#refresh());
  }
  disconnectedCallback() {
    this.dialog?.close();
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
  #documentClick = (event) => {
    const trigger = event.target instanceof Element && event.target.closest('[data-size-sheet-open]');
    if (trigger && this.picker?.contains(trigger)) { event.preventDefault(); this.open(trigger); }
  };
  #invalid = (event) => {
    if (event.target === this.select) { event.preventDefault(); this.open(this.picker.querySelector('[data-size-sheet-open]')); }
  };
  #onSelected = (event) => {
    if (event.target !== this.picker) return;
    this.#busy = true;
    this.#refresh();
  };
  #onUpdated = (event) => {
    if (event.target !== this.picker) return;
    this.#busy = false;
    queueMicrotask(() => this.#refresh());
  };
  #onError = (event) => {
    if (event.target !== this.picker) return;
    this.#busy = true;
    this.querySelector('[data-sheet-status]').textContent = this.dataset.error;
    this.querySelector('[data-sheet-add]').disabled = true;
  };
  open(trigger) {
    if (!this.select) return;
    this.#returnFocus = trigger;
    this.#refresh();
    if (!this.dialog.open) this.dialog.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
  }
  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-sheet-close]')) { this.dialog.close(); return; }
    if (event.target === this.dialog) {
      const r = this.dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      return;
    }
    const option = event.target.closest('[data-sheet-option]');
    if (option && this.select) {
      this.select.value = option.dataset.sheetOption;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    if (event.target.closest('[data-sheet-add]')) {
      const button = this.mainButton;
      if (this.#busy || !this.select?.value || !button || button.disabled) return;
      if (!button.form?.reportValidity()) return;
      this.dialog.close();
      button.click();
    }
  };
  #refresh() {
    const select = this.select;
    if (!select) return;
    const wrapper = select.closest('.variant-option__select-wrapper');
    const trigger = wrapper?.querySelector('[data-size-sheet-open]');
    if (!trigger) return;
    const text = select.value || this.dataset.choose;
    const triggerText = trigger.querySelector('[data-size-sheet-label]');
    if (triggerText.textContent !== text) triggerText.textContent = text;
    trigger.hidden = false;
    wrapper.dataset.vivazzSheetReady = 'true';
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    const color = this.picker.querySelector('[data-vivazz-color] .variant-option__swatch-value')?.textContent.trim() || '';
    this.querySelector('[data-sheet-color]').textContent = color;
    const container = this.querySelector('[data-sheet-options]');
    const focused = this.contains(document.activeElement) ? document.activeElement?.dataset.sheetOption : null;
    const rows = Array.from(select.options).filter(o => o.dataset.optionValueId).map(o => {
      const row = document.createElement('div');
      row.className = 'vivazz-size-sheet__row';
      const available = o.dataset.optionAvailable !== 'false';
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.sheetOption = o.value;
      button.className = 'vivazz-size-sheet__option';
      button.disabled = !available;
      button.setAttribute('aria-pressed', String(o.selected));
      const label = document.createElement('span'); label.textContent = o.value;
      const mark = document.createElement('span'); mark.setAttribute('aria-hidden', 'true'); mark.textContent = o.selected ? '✓' : '';
      button.append(label, mark); row.append(button);
      if (!available) {
        row.classList.add('is-unavailable');
        const request = document.createElement('a');
        const subject = `Availability request: ${this.dataset.productTitle} — ${color} — ${o.value}`;
        request.href = `mailto:info@vivazzsports.com?subject=${encodeURIComponent(subject)}`;
        request.textContent = this.dataset.notify;
        request.title = 'Email our team to request an availability update.';
        const bell = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        bell.setAttribute('viewBox', '0 0 24 24');
        bell.setAttribute('width', '18');
        bell.setAttribute('height', '18');
        bell.setAttribute('fill', 'none');
        bell.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
        bell.append(path); request.append(bell);
        request.setAttribute('aria-label', `${this.dataset.notify}: ${o.value}, ${color}`);
        row.append(request);
      }
      return row;
    });
    container.replaceChildren(...rows);
    if (focused && this.dialog.open) container.querySelector(`[data-sheet-option="${CSS.escape(focused)}"]`)?.focus({ preventScroll: true });
    const available = select.selectedOptions[0]?.dataset.optionAvailable !== 'false';
    const add = this.querySelector('[data-sheet-add]');
    add.disabled = this.#busy || !select.value || !available || !this.mainButton || this.mainButton.disabled;
    add.textContent = this.#busy ? this.dataset.updating : !select.value ? this.dataset.choose : !available ? this.dataset.unavailable : this.dataset.add;
    this.querySelector('[data-sheet-status]').textContent = this.#busy ? this.dataset.updating : '';
  }
}
if (!customElements.get('vivazz-size-sheet')) customElements.define('vivazz-size-sheet', VivazzSizeSheet);
